import { useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, Search, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/design-system/primitives";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/cn";
import { useSupplementCatalog, type SupplementProduct } from "./api";
import {
  filterSupplements,
  isPurchasableSupplement,
  SUPPLEMENT_CATEGORIES,
  supplementPrice,
  type SupplementSort,
} from "./catalog";
import { SupplementDetail, SupplementImage } from "./SupplementDetail";
import { SupplementOrders } from "./SupplementOrders";
import {
  SUPPLEMENT_ANALYTICS_CONTEXT,
  supplementProductProperties,
} from "./analytics";

export function SupplementsShop() {
  const query = useSupplementCatalog();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState<SupplementSort>("featured");
  const [selected, setSelected] = useState<SupplementProduct | null>(null);
  const [productNotice, setProductNotice] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // A shared Radix trigger ref points at the last card in the collection.
  // Remember the actual opener so closing details never scrolls to that card.
  const detailOpener = useRef<HTMLButtonElement | null>(null);
  const [searchParams] = useSearchParams();
  const catalogViewed = useRef(false);
  const products = useMemo(
    () => (query.data?.products ?? []).filter(isPurchasableSupplement),
    [query.data?.products],
  );
  const selectedProduct = selected
    ? (products.find((product) => product.id === selected.id) ?? null)
    : null;
  const visible = useMemo(
    () => filterSupplements(products, search, category, sort),
    [products, search, category, sort],
  );
  const categories = Object.entries(SUPPLEMENT_CATEGORIES).filter(([key]) =>
    products.some((p) => p.category === key),
  );
  useEffect(() => {
    if (!selected || !query.data || selectedProduct) return;
    setSelected(null);
    setProductNotice(
      query.data.enabled
        ? `${selected.title} is no longer available. Please choose another supplement.`
        : null,
    );
  }, [selected, selectedProduct, query.data]);
  useEffect(() => {
    if (catalogViewed.current || !query.data?.enabled) return;
    catalogViewed.current = true;
    const properties = {
      ...SUPPLEMENT_ANALYTICS_CONTEXT,
      tab: "supplements",
      product_count: products.length,
      products: products.map(supplementProductProperties),
    };
    // The shop mounts only while this tab is selected. Catalog refreshes and
    // filters do not count another visit; leaving and reentering does.
    track("supplement_tab_viewed", properties);
    track("supplement_catalog_viewed", properties);
  }, [query.data, products]);

  function selectProduct(
    product: SupplementProduct,
    opener: HTMLButtonElement,
  ) {
    detailOpener.current = opener;
    setProductNotice(null);
    setSelected(product);
    track("supplement_product_viewed", {
      ...SUPPLEMENT_ANALYTICS_CONTEXT,
      ...supplementProductProperties(product),
    });
  }

  const reset = () => {
    setSearch("");
    setCategory("all");
    setSort("featured");
  };
  const orderId = searchParams.get("supplementOrder");
  return (
    <div
      className="px-15 pt-15 sm:pt-20 pb-40 flex flex-col gap-15 sm:gap-25"
      data-tour="shop-supplements-grid"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-15 border-b border-line-subtle pb-15 sm:pb-25">
        <div className="max-w-[62rem]">
          <p className="hidden sm:block type-label-xs text-ink-secondary mb-10">
            Your daily routine
          </p>
          <h2
            ref={heading}
            tabIndex={-1}
            className="font-display text-20 sm:text-28 leading-110 focus:outline-none"
          >
            Supplements for your everyday.
          </h2>
          <p className="type-body-md text-ink-secondary leading-140 mt-12">
            Protein, fiber, hydration, and daily basics from established brands.
            One-time purchases, with no subscription.
          </p>
        </div>
        <p className="hidden md:block type-label-xs text-ink-secondary md:text-right shrink-0 leading-140">
          One-time purchases
          <br />
          No subscription
        </p>
      </div>
      {orderId && <SupplementOrders highlightedOrderId={orderId} compact />}
      {productNotice && (
        <p role="status" className="type-body-sm p-15 bg-raised">
          {productNotice}
        </p>
      )}
      {searchParams.get("checkout") === "cancelled" && (
        <p role="status" className="type-body-sm p-15 bg-raised">
          Checkout was cancelled. You can review a product and try again.
        </p>
      )}
      {query.isLoading ? (
        <div
          role="status"
          aria-label="Fetching supplements"
          className="grid grid-cols-2 xl:grid-cols-3 gap-20"
        >
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={index}
              className="h-[32rem] bg-raised motion-safe:animate-pulse"
            />
          ))}
        </div>
      ) : query.isError ? (
        <div className="border border-line-subtle p-25">
          <p role="alert" className="type-body-md">
            We could not retrieve the supplement collection.
          </p>
          <Button
            variant="secondary"
            className="mt-15"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : !query.data?.enabled ? (
        <p
          role="status"
          className="p-25 border border-line-subtle type-body-md"
        >
          The supplements shop is currently unavailable.
        </p>
      ) : products.length === 0 ? (
        <div className="border border-line-subtle p-25">
          <h3 className="type-label-sm">No supplements available right now.</h3>
          <p className="type-body-md text-ink-secondary mt-10">
            Please check back shortly for available products.
          </p>
          <Button
            variant="secondary"
            className="mt-15"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : (
        <Dialog.Root
          open={Boolean(selectedProduct)}
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[24rem_minmax(0,1fr)] gap-20 items-start">
            <aside
              aria-label="Supplement categories"
              className="lg:sticky lg:top-20 border border-line-subtle"
            >
              <h3 className="hidden lg:block type-label-sm p-15 border-b border-line-subtle">
                Shop by category
              </h3>
              <div className="flex lg:flex-col overflow-x-auto p-5">
                {[["all", "All supplements"], ...categories].map(
                  ([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={category === key}
                      onClick={() => setCategory(key)}
                      className={cn(
                        "flex items-center justify-between gap-15 min-h-44 px-10 type-body-sm text-left whitespace-nowrap focus-visible:shadow-focus",
                        category === key
                          ? "bg-accent-wash text-accent"
                          : "hover:bg-raised text-ink-secondary",
                      )}
                    >
                      {label}
                      <span className="font-mono text-10">
                        {key === "all"
                          ? products.length
                          : products.filter((p) => p.category === key).length}
                      </span>
                    </button>
                  ),
                )}
              </div>
              <p className="hidden lg:block p-15 type-body-sm text-ink-secondary leading-140 border-t border-line-subtle">
                Review product labels and quality information to find what fits
                your routine.
              </p>
            </aside>
            <section
              className="min-w-0 flex flex-col gap-15"
              aria-label="Supplement collection"
            >
              <div className="flex flex-col sm:flex-row gap-10">
                <label className="relative flex-1 min-w-0">
                  <span className="sr-only">Search supplements</span>
                  <Search
                    size={18}
                    aria-hidden
                    className="absolute left-15 top-1/2 -translate-y-1/2 text-ink-secondary"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search supplements"
                    className="w-full h-48 bg-raised border border-line-subtle pl-44 pr-44 text-16 md:text-14 focus-visible:outline-none focus-visible:shadow-focus"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      aria-label="Clear supplement search"
                      className="absolute right-2 top-2 min-h-44 w-44 flex items-center justify-center focus-visible:shadow-focus"
                    >
                      <X size={16} />
                    </button>
                  )}
                </label>
                <label className="sm:w-[19rem]">
                  <span className="sr-only">Sort supplements</span>
                  <select
                    value={sort}
                    onChange={(event) =>
                      setSort(event.target.value as SupplementSort)
                    }
                    className="w-full h-48 px-10 border border-line-subtle bg-raised type-label-xs focus-visible:shadow-focus"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-asc">Price: low to high</option>
                    <option value="price-desc">Price: high to low</option>
                    <option value="name">Name: A to Z</option>
                  </select>
                </label>
              </div>
              <div className="flex justify-between gap-10 min-h-44 items-center">
                <p role="status" className="type-body-sm text-ink-secondary">
                  {visible.length} supplement{visible.length === 1 ? "" : "s"}
                </p>
                {(search || category !== "all") && (
                  <Button variant="ghost" className="min-h-44" onClick={reset}>
                    Clear filters
                  </Button>
                )}
              </div>
              {visible.length === 0 ? (
                <div className="p-25 bg-raised">
                  <h3 className="type-label-sm">No matching supplements.</h3>
                  <p className="type-body-md mt-10 text-ink-secondary">
                    Try another brand, ingredient, or category.
                  </p>
                  <Button variant="secondary" className="mt-15" onClick={reset}>
                    Show all supplements
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-25 sm:gap-20">
                  {visible.map((product) => (
                    <article
                      key={product.id}
                      className="flex flex-col border border-line-subtle min-w-0 bg-page"
                    >
                      <button
                        type="button"
                        onClick={(event) =>
                          selectProduct(product, event.currentTarget)
                        }
                        aria-haspopup="dialog"
                        className="text-left flex flex-col flex-1 focus-visible:shadow-focus"
                        aria-label={`View ${product.brand} ${product.title}`}
                      >
                        <div className="relative aspect-square w-full shrink-0 bg-raised">
                          <SupplementImage
                            key={product.imageUrl}
                            product={product}
                            className="absolute inset-0 w-full h-full p-15 sm:p-25 mix-blend-multiply"
                          />
                        </div>
                        <div className="p-12 sm:p-15 flex flex-col gap-8 flex-1">
                          <p className="type-label-xs text-ink-secondary">
                            {product.brand}
                          </p>
                          <h3 className="font-display text-16 sm:text-18 leading-120">
                            {product.title}
                          </h3>
                          <p className="type-body-sm text-ink-secondary leading-140">
                            {product.size}
                          </p>
                          <p className="hidden sm:block type-body-sm text-ink-secondary leading-140">
                            {product.shortDescription}
                          </p>
                          <div className="mt-auto pt-10">
                            <p className="font-mono text-16 tabular-nums">
                              {supplementPrice(product.priceCents)}
                            </p>
                            <p className="type-label-xs text-ink-secondary mt-5">
                              One-time
                            </p>
                          </div>
                        </div>
                      </button>
                      <Button
                        variant="secondary"
                        size="lg"
                        className="w-full justify-between !px-12 sm:!px-15 !text-14 border-x-0 border-b-0"
                        onClick={(event) =>
                          selectProduct(product, event.currentTarget)
                        }
                        aria-haspopup="dialog"
                        aria-label={`Shop ${product.brand} ${product.title}`}
                      >
                        View product
                        <ArrowRight size={16} aria-hidden />
                      </Button>
                    </article>
                  ))}
                </div>
              )}
              <p className="type-body-sm text-ink-secondary leading-140 border-t border-line-subtle pt-20">
                Prices and availability are checked again before payment. Review
                product labels for current ingredients and directions.
              </p>
            </section>
          </div>
          {selectedProduct && (
            <SupplementDetail
              key={selectedProduct.id}
              product={selectedProduct}
              checkoutEnabled={query.data.checkoutEnabled}
              onAdded={() => {
                detailOpener.current = null;
                setSelected(null);
              }}
              onReturnFocus={() => {
                if (detailOpener.current?.isConnected) {
                  detailOpener.current.focus({ preventScroll: true });
                } else if (
                  detailOpener.current &&
                  heading.current?.isConnected
                ) {
                  heading.current.focus({ preventScroll: true });
                }
              }}
            />
          )}
        </Dialog.Root>
      )}
    </div>
  );
}
